import React, { Dispatch, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, SxProps } from '@mui/material';
import { headingFontBold, vars } from '../../../../../Theme';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import { useParams } from 'react-router-dom';
import {
    ICreateReportFromTemplateErrorState,
    ICreateReportFromTemplateMainState,
} from '../CreateReportDialog';
import {
    CreateReportFromTemplateDialogEnum,
    onConfirmCreateReport,
} from '../../../../../redux/project/documents/sliceProjectReportTemplate';
import dayjs from 'dayjs';
import { ToastSeverity } from '../../../Toast/Toast';
import { fetchViewDataAndDocuments } from '../../../../../util/ProjectUtils';
import { useEnqueueSnackbar } from '../../../../../hooks/useEnqueueSnackbar';
import { updateSelectedDocuments } from '../../../../../redux/project/documents/sliceProjectSelectedDocuments';

const buttonSx: SxProps = {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: headingFontBold,
    lineHeight: 'normal',
    padding: '9px 12px',
    color: vars.white,
};

export default ({ setShowErrorState, mainState, onClose }: ICreateReportDialogActionsProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'createReportDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const params = useParams();

    const { replicaId, areaName } = params;

    const { loading: isLoading, typeOfDialog } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsReportFromTemplate,
    );

    const { data: documents } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );

    const isConfirmButtonDisabled = useMemo(() => {
        return typeOfDialog === CreateReportFromTemplateDialogEnum.SelectedDocuments
            ? !documents?.length || isLoading
            : isLoading;
    }, [documents, isLoading, typeOfDialog]);

    const getFileName = () =>
        t('reportFileName', { dateAndTime: dayjs().format('YYYYMMDDHHmmss') });

    const validateInput = () => {
        let sendToError = false;

        if (typeOfDialog === CreateReportFromTemplateDialogEnum.Database) {
            sendToError = mainState.sendTo.length === 0;
        }

        setShowErrorState({
            selectedDocumentTypes: mainState.selectedDocumentTypes.length === 0,
            sendTo: sendToError,
        });

        return mainState.selectedDocumentTypes.length !== 0 && !sendToError;
    };

    const onCreateReport = () => {
        if (validateInput()) {
            const fileName = getFileName();
            dispatch(onConfirmCreateReport({ replicaId, ...mainState, fileName }));

            if (typeOfDialog === CreateReportFromTemplateDialogEnum.SelectedDocuments) {
                dispatch(
                    updateSelectedDocuments({
                        replicaId,
                        areaName,
                        documentIds: [],
                    }),
                );
                fetchViewDataAndDocuments(params);

                enqueueSnackbar(t('toastInfo', { numberOfDocuments: documents.length }), {
                    severity: ToastSeverity.Info,
                });
            } else {
                enqueueSnackbar(t('databaseToastInfo'), {
                    severity: ToastSeverity.Info,
                });
            }
            onClose();
        }
    };

    return (
        <DialogActions sx={{ px: 3, py: 1, borderTop: `1px solid ${vars.lightGray}` }}>
            <Button
                variant={'text'}
                onClick={onClose}
                sx={{ ...buttonSx, color: vars.font }}
                disabled={isLoading}
            >
                {t('ctaCancel')}
            </Button>
            <Button
                variant={'contained'}
                onClick={onCreateReport}
                sx={buttonSx}
                disabled={isConfirmButtonDisabled}
            >
                {t('ctaSend')}
            </Button>
        </DialogActions>
    );
};

interface ICreateReportDialogActionsProps {
    setShowErrorState: Dispatch<React.SetStateAction<ICreateReportFromTemplateErrorState>>;
    mainState: ICreateReportFromTemplateMainState;
    onClose: () => void;
}
